import React from "react";
import { Updater } from "use-immer";
import {
  type EacoDocumentContent,
  type ResolvedFieldMarks,
  formStaticCssVars,
  formCompactCssVars,
} from "@eaco/forms-core";

import { DocumentProvider } from "./DocumentContext";
import { FieldMarksProvider } from "./FieldMarksContext";
import DocumentElement from "./DocumentElement";
import { cn } from "../lib/utils";

// Static forms design tokens as CSS vars. Injected here so every surface that
// renders a DocumentView (fill, editor Preview, read-only viewer) resolves
// `var(--forms-*)` even without a per-form FormThemeProvider. Constant, so
// nesting inside a FormThemeProvider never clobbers the per-form accent.
const STATIC_TOKEN_VARS = formStaticCssVars() as React.CSSProperties;
// Same tokens but with the compact field-sizing overrides, for dense hosts
// (e.g. the editor's element-config sidebar).
const COMPACT_TOKEN_VARS = {
  ...formStaticCssVars(),
  ...formCompactCssVars(),
} as React.CSSProperties;

interface Props {
  eacoDocument?: EacoDocumentContent;
  values: Record<string, unknown>;
  updateValues: Updater<Record<string, unknown>>;
  /**
   * Render in viewer mode — hides field-level action chrome (Edit /
   * Clear / Delete row buttons, autocomplete affordances, etc.). The
   * answers themselves still render so the user can read them.
   */
  isReadOnly?: boolean;
  /**
   * Render for PDF/print (server-side gotenberg). Pages become hard page
   * breaks instead of on-screen "paper" cards. Implies isReadOnly.
   */
  isPrint?: boolean;
  /**
   * Read-only preview of a blank form: inputs show their configured
   * defaults as the filler would see them. Leave off when viewing a
   * response, so unanswered fields read as unanswered.
   */
  previewDefaults?: boolean;
  /**
   * Field density. "compact" shrinks input padding/font + label size (via CSS
   * vars) without changing colours/borders — for dense hosts like the editor's
   * element-config sidebar. Defaults to "default" (the on-form size).
   */
  density?: "default" | "compact";
  /**
   * Optional per-element annotation slot — its return renders inline
   * under each leaf input element. Used by the sign-off review to place
   * reviewer comments next to each answer.
   */
  renderElementAnnotation?: (elementId: string) => React.ReactNode;
  /**
   * How field titles mark required and optional questions when the
   * document does not say (see forms-core fieldMarks): a filled form's
   * defaults unless the host passes an element type's configuration
   * form's.
   */
  fieldMarkDefaults?: ResolvedFieldMarks;
}

/**
 * Renders an `EacoDocumentContent` tree using whatever
 * `ElementRegistry` the host has mounted above this view. The
 * registry-based dispatch is the seam that lets the same engine
 * serve apps/web (full Radix/shadcn renderer set), apps/forms
 * (anonymous-fill subset), and (later) the read-only viewer.
 */
const DocumentView: React.FC<Props> = ({
  eacoDocument,
  fieldMarkDefaults,
  values,
  updateValues,
  isReadOnly = false,
  isPrint = false,
  previewDefaults = false,
  density = "default",
  renderElementAnnotation,
}) => {
  if (!eacoDocument) {
    return null;
  }
  // A form whose sole top-level element is a Pages stepper drives its own
  // full-height layout (sticky page-nav footer). Let the container take the
  // host's remaining height so short/empty pages still pin the footer to the
  // bottom instead of collapsing up under the content. It must share that
  // height as a flex item (not claim 100% of it): the fill dialog stacks the
  // voice strip above the form, and a 100% min-height pushed the stepper's
  // footer below the dialog's clipped edge.
  const isSinglePaged =
    !isReadOnly &&
    eacoDocument.elements.length === 1 &&
    eacoDocument.elements[0]?.typeId === "pages";
  return (
    <DocumentProvider
      document={eacoDocument}
      isEditor={false}
      isReadOnly={isReadOnly || isPrint}
      isPrint={isPrint}
      previewDefaults={previewDefaults}
      values={values}
      updateValues={updateValues}
      renderElementAnnotation={renderElementAnnotation}
    >
      <FieldMarksProvider content={eacoDocument} defaults={fieldMarkDefaults}>
      <div
        className={cn(isSinglePaged ? "flex min-h-0 flex-1 flex-col" : "space-y-4")}
        style={{
          ...(density === "compact" ? COMPACT_TOKEN_VARS : STATIC_TOKEN_VARS),
          // Report accent/tint (entity report documents) — renderers read
          // var(--report-accent, <fallback>) so untinted docs are unchanged.
          ...(eacoDocument.settings?.accentColor
            ? ({ "--report-accent": eacoDocument.settings.accentColor } as Record<string, string>)
            : {}),
        }}
      >
        {eacoDocument.elements.map((element) => (
          <DocumentElement key={element.id} element={element} />
        ))}
      </div>
      </FieldMarksProvider>
    </DocumentProvider>
  );
};

export default DocumentView;
export { DocumentElement };
